import { Box, Button, Checkbox, FormGroup, Heading, Modal, SuccessAnimation, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOption } from '@frontend/components/interfaces/option';
import { GeneralSettingsSpecialization } from '@frontend/components/pages/personalSettings/personalSettingsGeneralSettings/GeneralSettingsSpecialization';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';

interface IAskEmploymentForSpecializationsModalProps {
    employmentId: string;
}

export function AskEmploymentForSpecializationsModal(props: IAskEmploymentForSpecializationsModalProps) {
    const abortableFetch = useAbortableFetch();
    const { dispatchUiStateAction } = useUiStateContext();
    const [isOpen, setIsOpen] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [noSpecializations, setNoSpecializations] = useState(false);
    const [selectedSpecializations, setSelectedSpecializations] = useState<IOption<string>[]>([]);

    useEffect(() => {
        shouldAskEmploymentForSpecializations();
    }, []);

    useEffect(() => {
        dispatchUiStateAction({ type: 'setIsAskEmploymentForSpecializationsModalOpen', isModalOpen: isOpen });
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={showSuccess ? 'small' : 'large'}
        >
            {showSuccess ? renderSuccessAnimation() : renderAskSpecializations()}
        </Modal>
    );

    function renderAskSpecializations() {
        return (
            <>
                <Modal.CloseButton />
                <Modal.Header>{translateKey('askEmploymentForSpecializationsModal.title')}</Modal.Header>
                <Modal.Body>
                    <Text>{translateKey('askEmploymentForSpecializationsModal.description')}</Text>
                    <Box
                        id="AskSpecializationsMod"
                        mt={4}
                    >
                        <GeneralSettingsSpecialization
                            currentSpecializations={selectedSpecializations}
                            employmentId={props.employmentId}
                            isDisabled={noSpecializations}
                            isEditing={true}
                            onChange={(value: any) => setSelectedSpecializations(value)}
                            placeHolderMessage={translateKey('askEmploymentForSpecializationsModal.placeHolderMessage')}
                            showTooltip={false}
                        />
                        <FormGroup>
                            <Checkbox
                                checked={noSpecializations}
                                disabled={selectedSpecializations.length > 0}
                                label={translateKey('askEmploymentForSpecializationsModal.noSpecializations')}
                                onChange={(event) => setNoSpecializations(event.target.checked)}
                            />
                        </FormGroup>
                    </Box>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        onClick={() => handleClose()}
                        variant="outline"
                    >
                        {translateKey('common.cancel')}
                    </Button>
                    <Button
                        isDisabled={noSpecializations === false && selectedSpecializations.length === 0}
                        onClick={async () => await handleSubmit()}
                    >
                        {translateKey('common.save')}
                    </Button>
                </Modal.Footer>
            </>
        );
    }

    function renderSuccessAnimation() {
        return (
            <>
                <Modal.CloseButton />
                <Modal.Body>
                    <Box mt={4}>
                        <Heading
                            mb={4}
                            mt={4}
                            size="large"
                            textAlign="center"
                        >
                            {translateKey('organization.successful')}
                        </Heading>
                        <Text textAlign="center">
                            {translateKey('askEmploymentForSpecializationsModal.thanksMessage')}
                        </Text>
                        <Box
                            display="flex"
                            justifyContent="center"
                            mt={4}
                        >
                            <SuccessAnimation size="large" />
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => {
                            setShowSuccess(false);
                            setIsOpen(false);
                        }}
                        width="full"
                    >
                        {translateKey('common.close')}
                    </Button>
                </Modal.Footer>
            </>
        );
    }

    async function handleSubmit() {
        if (noSpecializations) {
            handleClose(noSpecializations);

            return;
        }

        if (!selectedSpecializations) {
            return;
        }

        const url = `/api/employments/${props.employmentId}/specializations`;
        const employmentSpecializations = selectedSpecializations.map(
            (specialization: IOption<string>) => specialization.value
        );

        await abortableFetch.fetchJson(url, 'POST', employmentSpecializations);

        setShowSuccess(true);
    }

    async function handleClose(hasNoSpecializations?: boolean) {
        const url = `/api/employments/${props.employmentId}/askEmploymentForSpecializations${
            hasNoSpecializations ? '?hasNoSpecializations=true' : ''
        }`;

        await abortableFetch.postJson(url, {});

        setIsOpen(false);
    }

    async function shouldAskEmploymentForSpecializations() {
        const result = await abortableFetch.getJson(
            `/api/employments/${props.employmentId}/askEmploymentForSpecializations`
        );

        setIsOpen(result.json.shouldAskForSpecializations);
    }
}
